import React from 'react';
import { Link } from 'react-router-dom';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import './NotFound.css';

interface NotFoundProps {
    /** What was being looked for, e.g. "collage". Omit for a plain 404. */
    thing?: string;
    /** Where to send the visitor back to. */
    backTo?: { label: string; path: string };
}

/**
 * Shared not-found state.
 *
 * The detail pages used to bail out with a bare unstyled
 * `<div>Collage not found</div>` — no heading, no breadcrumb, no way back —
 * and an unknown URL rendered an empty page with just the navbar.
 */
const NotFound: React.FC<NotFoundProps> = ({
    thing,
    backTo = { label: 'home', path: '/' }
}) => {
    useDocumentMeta({
        title: thing ? `${thing} not found` : 'Not found',
        description: 'That page could not be found.'
    });

    return (
        <div className="not-found">
            <p className="not-found-code">404</p>
            <h1 className="not-found-title">
                {thing ? `That ${thing} isn't here` : "That page isn't here"}
            </h1>
            <p className="not-found-body">
                The link may be out of date, or the {thing || 'page'} may have been
                renamed. Nothing is lost — it just isn't at this address.
            </p>
            <div className="not-found-actions">
                <Link to={backTo.path} className="not-found-link">
                    ← back to {backTo.label}
                </Link>
                <Link to="/collages" className="not-found-link">
                    browse collages
                </Link>
            </div>
        </div>
    );
};

export default NotFound;
